import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { BehaviorSubject, from, of } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { filter, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LogoutTimer } from './logout.timer';

@Injectable({
    providedIn: 'root'
})
export class AuthKeycloakService {

    public isLoaded = false;
    private isAuthenticated = false;

    public readonly isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
    public userProfile$ = new BehaviorSubject<any>(null);
    public currentUserProfile: any = null;

    constructor(
        private readonly oauthService: OAuthService,
        private readonly router: Router,
        private readonly location: Location,
        private readonly logoutTimer: LogoutTimer
    ) {
    }

    public setupListeners(): void {
        this.isAuthenticatedSubject$
            .pipe(filter(loggedIn => {
                    return (this.isAuthenticated !== loggedIn);
                }),
                switchMap(loggedIn => (loggedIn && this.isLoaded) ? from(this.oauthService.loadUserProfile()) : of(null)))
            .subscribe({
                next: userProfile => this.applyUserProfile(userProfile),
                error: error => console.error(error)
            });

    }

    public shouldInvokeAuthSubject() {
        return (this.isAuthenticated !== this.oauthService.hasValidAccessToken()) || (this.currentUserProfile == null);
    }

    public attachEvents(): void {
        // Automatically load user profile
        this.oauthService.events
            .pipe(filter(e => e.type === 'token_received'))
            .subscribe(() => {
                if (this.oauthService.state) {
                    console.debug('Navigiere nach token_received Event zu:', decodeURIComponent(this.oauthService.state));
                    this.router.navigateByUrl(decodeURIComponent(this.oauthService.state));
                    this.oauthService.state = '';
                }
            });

        // Bugfix: KREDP-4784 In case of redirect after assign new password (forget password link). This avoids white page
        this.oauthService.events
            .pipe(filter(e => e.type === 'invalid_nonce_in_state'))
            .subscribe(() => {
                this.oauthService.initLoginFlow();
            });

        // In case user is logged out forcefully.
        this.oauthService.events
            .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
            .subscribe(() => {
                this.oauthService.state = '';
                this.navigateToLoginPage();
            });

        // In case keycloak server is dead.
        this.oauthService.events
            .pipe(filter(e => ['discovery_document_load_error', 'discovery_document_validation_error'].includes(e.type)))
            .subscribe(() => {
                console.error('Fehler beim Laden der Auth-Config');
            });
    }

    private applyUserProfile(userProfile: any) {
        console.debug('User-Profil geladen', userProfile);
        this.isAuthenticated = userProfile !== null;
        this.currentUserProfile = userProfile;
        this.userProfile$.next(userProfile);
    }

    public getUserProfile(): void {
        this.oauthService.loadUserProfile()
            .then(userProfile => {
                this.applyUserProfile(userProfile);
                this.logoutTimer.start()
                this.logoutTimer.timeoutObservable.subscribe(isTimeout => {
                    if (isTimeout) {
                        this.logout()
                    }
                })
                this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
            })
            .catch(err => console.error(err));
    }

    navigateToLoginPage(path?: string): Promise<boolean> {
        return this.oauthService.loadDiscoveryDocumentAndLogin({state: path || this.location.path()});
    }

    logout(): void {
        this.oauthService.revokeTokenAndLogout();
    }

    get accessToken(): string {
        return this.oauthService.getAccessToken();
    }

    forceLogin() {
        this.oauthService.initCodeFlow(this.location.path());
    }

}
